import { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Medal, Award } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Leaderboard() {
  const [leaderboards, setLeaderboards] = useState([]);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  useEffect(() => {
    if (selectedLeaderboard) {
      fetchLeaderboardEntries();
    }
  }, [selectedLeaderboard]);

  const fetchLeaderboards = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/leaderboards`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeaderboards(response.data);
      if (response.data.length > 0 && !selectedLeaderboard) {
        setSelectedLeaderboard(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboardEntries = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/leaderboards/${selectedLeaderboard}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard entries:', error);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-orange-500" />;
    return <span className="w-6 h-6 flex items-center justify-center font-bold">#{rank}</span>;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <h3 className="text-xl font-semibold flex items-center gap-2">
        <Trophy className="w-6 h-6" />
        Leaderboard
      </h3>

      {leaderboards.length > 1 && (
        <div>
          <label className="block text-sm font-medium mb-2">Select Leaderboard</label>
          <select
            value={selectedLeaderboard || ''}
            onChange={(e) => setSelectedLeaderboard(parseInt(e.target.value))}
            className="w-full border rounded px-3 py-2"
          >
            {leaderboards.map((lb) => (
              <option key={lb.id} value={lb.id}>
                {lb.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">Loading leaderboard...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No entries yet</div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry, index) => (
            <div
              key={entry.agent_id}
              className={`border rounded p-4 flex items-center justify-between ${
                entry.rank <= 3 ? 'bg-yellow-50 border-yellow-200' : ''
              }`}
            >
              <div className="flex items-center gap-4">
                {getRankIcon(entry.rank)}
                <div>
                  <p className="font-semibold">{entry.agent_name}</p>
                  <p className="text-sm text-gray-600">Score: {entry.score?.toFixed(2) || 0}</p>
                </div>
              </div>
              {entry.metrics && (
                <div className="text-right">
                  {Object.entries(entry.metrics).map(([key, value]) => (
                    <p key={key} className="text-xs text-gray-500">
                      {key}: {value}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

